import { TypedDocumentNode } from '@graphql-typed-document-node/core'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SaveButton } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/components/SaveButton'
import {
  FormattedAssignment,
  FormattedAssignmentWithDeletion,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'
import { useCostCentersMutations } from 'modules/cost_centers/hooks/useCostCentersMutations'

interface Props {
  assignments: FormattedAssignment[]
  assignmentsToUpdate?: FormattedAssignmentWithDeletion[]
  disabled: boolean
  resourceId: number
  resourceType: string
  onSave?: () => Promise<void>
  refetchQueries?: TypedDocumentNode[]
}

export const CreateButton = ({
  assignments,
  assignmentsToUpdate = [],
  disabled,
  resourceId,
  resourceType,
  onSave,
  refetchQueries,
}: Props) => {
  const { create, save } = useCostCentersMutations(refetchQueries)

  const handleOnClick = async () => {
    // If we have assignmentsToUpdate (for structured mode), use save() instead of create()
    const hasAssignmentsToUpdate = assignmentsToUpdate.length > 0

    let successSave: boolean

    if (hasAssignmentsToUpdate) {
      successSave = await save({
        assignmentsToCreate: assignments.map((assignment) => ({
          resourceId: resourceId,
          resourceType: resourceType,
          costCenterId: assignment.costCenterId ? Number(assignment.costCenterId) : undefined,
          percentage: assignment.percentage,
          startDate: assignment.startDate,
          endDate: assignment.endDate === '' ? undefined : assignment.endDate,
          isDefault: assignment.isDefault || false,
        })),
        assignmentsToUpdate: assignmentsToUpdate.map((assignment) => ({
          id: Number(assignment.id),
          costCenterId: assignment.costCenterId ? Number(assignment.costCenterId) : undefined,
          percentage: assignment.percentage,
          startDate: assignment.startDate,
          endDate: assignment.endDate === '' ? undefined : assignment.endDate,
          markedForDeletion: !!assignment.markedForDeletion,
        })),
      })
    } else {
      successSave = await create({
        assignments: assignments.map((assignment) => ({
          resourceId: resourceId,
          resourceType: resourceType,
          costCenterId: Number(assignment.costCenterId),
          percentage: assignment.percentage,
          startDate: assignment.startDate,
          endDate: assignment.endDate === '' ? undefined : assignment.endDate,
        })),
      })
    }

    if (successSave) {
      track(trackingEvents.accounting.saveCostCenterAssignment, {
        source: 'new_assignment',
      })

      await onSave?.()
    }
  }

  return (
    <SaveButton
      disabled={disabled}
      label={i18n.t('finance.cost_centers.assignments.create.button')}
      onSave={handleOnClick}
    />
  )
}
